import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  FetchIngredientDataQuery,
  FetchIngredientDataQueryVariables,
} from 'notebook/integrations/registry/__generated/FetchIngredientDataQuery';
import {MixtureIngredientsStructuredTableRowMetadata} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  getTypedValueFromBioentitySummary,
  getTypedValueFromStorableSummaries,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type IngredientData = ExtractByTypename<FetchIngredientDataQuery['ingredient'], 'IngredientNode'>;

export const FETCH_INGREDIENT_DATA_QUERY: DocumentNode = gql`
  query FetchIngredientDataQuery($ingredientId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    ingredient(id: $ingredientId) {
      ... on IngredientNode {
        id
        amount
        units
        catalogIdentifier
        componentLotValues {
          text
          bioentity {
            id
            name
            displayLabel
          }
          container {
            id
            name
          }
        }
        componentBioentity {
          id
          name
          displayLabel
        }
        notes
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const IngredientStructuredTableRowUpdater: StructuredTableRowUpdater<
  IngredientData,
  MixtureIngredientsStructuredTableRowMetadata
> = {
  fetchModel: ({
    idForLookup: apiIdentifier,
  }: SampleLookupInfo): Promise<IngredientData | InaccessibleInfoGraphQL> =>
    doQuerySafe<FetchIngredientDataQuery, FetchIngredientDataQueryVariables>(
      {query: FETCH_INGREDIENT_DATA_QUERY},
      {variables: {ingredientId: apiIdentifier}, fetchPolicy: 'network-only'}
    ).then((data: FetchIngredientDataQuery) => data.ingredient),

  // TODO(BNCH-104240): Convert to use bulk query
  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<IngredientData | InaccessibleInfoGraphQL>> {
    return Promise.all(sampleLookupInfos.map((info) => this.fetchModel(info))) as Promise<
      Array<IngredientData | InaccessibleInfoGraphQL>
    >;
  },

  updateCell: (
    model: IngredientData | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    const ingredient = model;
    if (isInaccessible(ingredient)) {
      return 'NO_VALUE';
    }
    const {amount, catalogIdentifier, componentBioentity, componentLotValues, units, notes} = ingredient;

    switch (column.columnType) {
      case TableColumnType.INGREDIENT_CATALOG_ID:
        return catalogIdentifier != null ? toStringTypedValue(catalogIdentifier) : null;

      case TableColumnType.MEASUREMENT_AMOUNT:
        if (column.columnGroup === StructuredTableColumnGroups.INGREDIENT_MEASURED_AMOUNT) {
          return amount != null ? toStringTypedValue(amount) : null;
        }
        return 'NO_VALUE';

      case TableColumnType.UNIT_SELECTOR:
        return units != null ? toStringTypedValue(units) : null;

      case TableColumnType.EXISTING_BIOENTITY:
        // Component lot entity
        if (column.columnGroup === StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE) {
          return componentLotValues.bioentity != null
            ? getTypedValueFromBioentitySummary(componentLotValues.bioentity)
            : null;
        }
        // Component entity
        if (column.columnGroup == null) {
          return componentBioentity != null ? getTypedValueFromBioentitySummary(componentBioentity) : null;
        }
        return 'NO_VALUE';

      case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
        return componentLotValues.text ? toStringTypedValue(componentLotValues.text) : null;

      case TableColumnType.CONTAINERS:
        if (column.columnGroup === StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE) {
          return componentLotValues.container
            ? getTypedValueFromStorableSummaries([componentLotValues.container])
            : getTypedValueFromStorableSummaries([]);
        }
        return 'NO_VALUE';

      case TableColumnType.INGREDIENT_NOTES:
        return notes != null ? toStringTypedValue(notes) : null;

      default:
        return 'NO_VALUE';
    }
  },

  updateRow(
    ingredient: IngredientData | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<MixtureIngredientsStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<MixtureIngredientsStructuredTableRowMetadata> {
    if (isInaccessible(ingredient) || row.metadata.ingredientId !== ingredient.id) {
      return row;
    }

    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      const liveValue = this.updateCell(ingredient, columnGroup, column);
      return updateCellWithLiveValue(cell, liveValue, {setCellBlankIfLiveValueNull: true});
    });

    return {
      ...row,
      cells,
    };
  },

  readFromCache: ({idForLookup: apiIdentifier}: SampleLookupInfo): IngredientData | null => {
    const data = readQuerySafe<FetchIngredientDataQuery, FetchIngredientDataQueryVariables>(
      {query: FETCH_INGREDIENT_DATA_QUERY},
      {variables: {ingredientId: apiIdentifier}}
    );
    if (data == null || isInaccessible(data.ingredient)) {
      return null;
    }
    return data.ingredient;
  },
};

export default IngredientStructuredTableRowUpdater;
